import { Component, computed, inject, resource, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTabsModule } from '@angular/material/tabs';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormControlMarkdownComponent } from '@components/form-control/markdown/markdown.component';
import {
  Attachment,
  AutocompleteOption,
  Dataset,
  DatasetContent,
  ItemListResponse,
  Label,
  Link,
  Organisation,
  Person,
  ScienceKeyword,
  Timeframe,
  LabelType,
} from '@models';
import ScienceKeywords from '@data/science-keywords.json';
import { FormControlAutocompleteComponent } from '@components/form-control/autocomplete/autocomplete.component';
import {
  AuthService,
  DatasetService,
  LabelService,
  NotificationService,
} from '@services';
import { MatCardModule } from '@angular/material/card';
import { LeafletGroupComponent } from '@components/leaflet/group/group.component';
import geojson from 'geojson';
import { MatListModule, MatListOption } from '@angular/material/list';
import { MatDialog } from '@angular/material/dialog';
import { HumanReadableSizePipe, RoleListStringPipe } from '@pipes';
import { ComponentType } from '@angular/cdk/overlay';
import { CommonModule, TitleCasePipe } from '@angular/common';
import { MatSelectModule } from '@angular/material/select';
import { HttpErrorResponse, httpResource } from '@angular/common/http';
import { environment } from '@environment';
import { ListComponent } from '@components/list/list.component';
import { MatPaginatorModule } from '@angular/material/paginator';
import { DatasetFormLinkComponent } from './link/link.component';
import { DatasetFormOrganisationComponent } from './organisation/organisation.component';
import { DatasetFormPersonComponent } from './person/person.component';
import { DatasetFormTimeframeComponent } from './timeframe/timeframe.component';
import { DatasetFormAttachmentCreateComponent } from './attachment/create/create.component';
import { DatasetFormAttachmentComponent } from './attachment/attachment.component';
import { DatasetFormAttachmentDeleteComponent } from './attachment/delete/delete.component';
import { CdkDragDrop, CdkDropList, CdkDrag } from '@angular/cdk/drag-drop';
import { form, FormField, maxLength, required, submit } from '@angular/forms/signals';
import { DatasetFormLabelComponent } from './label/label.component';
import { firstValueFrom } from 'rxjs';
import { SelectionModel } from '@angular/cdk/collections';

type ListProperty = 'timeframes' | 'people' | 'organisations' | 'links';

@Component({
  selector: 'app-dataset-form',
  imports: [
    RouterModule,
    CommonModule,

    FormField,

    CdkDropList,
    CdkDrag,

    MatButtonModule,
    MatIconModule,
    MatTabsModule,
    MatFormFieldModule,
    MatInputModule,
    MatCardModule,
    MatListModule,
    MatSelectModule,
    MatPaginatorModule,

    FormControlMarkdownComponent,
    FormControlAutocompleteComponent,
    LeafletGroupComponent,
    ListComponent,
    RoleListStringPipe,
    HumanReadableSizePipe,
    TitleCasePipe,
  ],
  templateUrl: './form.component.html',
  styleUrl: './form.component.scss',
})
export class DatasetFormComponent {
  dialog = inject(MatDialog);
  router = inject(Router);
  route = inject(ActivatedRoute);
  datasetService = inject(DatasetService);
  labelService = inject(LabelService);
  notificationService = inject(NotificationService);
  authService = inject(AuthService);

  dataset = signal(this.route.snapshot.data['dataset'] as Dataset | undefined);

  labelsPage = signal(0);
  labelsPageSize = signal(10);
  labels = httpResource<ItemListResponse<Label>>(() => this.dataset()
    ? `${environment.kinkoHost}/label/?skip=${this.labelsPage() * this.labelsPageSize()}&take=${this.labelsPageSize()}&count=true&datasetId=${this.dataset()?.id}`
    : undefined);

  harvesterLabels = computed(() => {
    return this.labels.value()?.items.filter(l => l.type === LabelType.Harvester) || [];
  });
  projectLabels = computed(() => {
    return this.labels.value()?.items.filter(l => l.type !== LabelType.Harvester) || [];
  });
  mayAddFiles = computed(() => {
    return this.dataset()?.type === 'draft' || this.authService.user()?.accessLevel === 'admin'
  });

  filesPage = signal(0);
  filesPageSize = signal(10);
  files = httpResource<ItemListResponse<Attachment>>(() =>
    this.dataset()
      ? `${environment.kinkoHost}/dataset/${this.dataset()?.id}/attachment/?skip=${this.filesPage() * this.filesPageSize()}&take=${this.filesPageSize()}&count=true&order=created:desc`
      : undefined);

  metadataModel = signal({
    title: this.dataset()?.content.title || '',
    summary: this.dataset()?.content.summary || '',
    keywords: this.dataset()?.content.keywords?.map(this.asKeywordAutocompleteOption)
      || null,
  });

  metadataForm = form(this.metadataModel,
    (schemaPath) => {
      required(schemaPath.title);
      required(schemaPath.summary);
      required(schemaPath.keywords);

      maxLength(schemaPath.title, 1024);
    });

  keywordOptions = (ScienceKeywords as ScienceKeyword[]).map(
    this.asKeywordAutocompleteOption,
  );
  keywordSearch = signal('');
  keywordResource = resource({
    params: () => ({ q: this.keywordSearch() }),
    loader: async (p) => {
      const results = [];
      const q = p.params.q.toLowerCase();

      for (const option of this.keywordOptions) {
        if (option.id.toLowerCase().includes(q)) {
          results.push(option);
        }

        if (results.length > 9) {
          break;
        }
      }
      return results;
    },
  });

  save(event: Event) {
    event.preventDefault()

    submit(this.metadataForm, async () => {
      const content = {
        ...this.dataset()?.content,
        title: this.metadataForm.title().value(),
        summary: this.metadataForm.summary().value(),
        keywords: this.metadataForm.keywords().value()?.map((o) => o.value) || [],
      } as DatasetContent;

      this.upsert(content);
    });
  }

  saveGeoJSON(geometry: geojson.FeatureCollection) {
    const content = {
      ...this.dataset()?.content,
      geojson: geometry,
    } as DatasetContent;

    if (!geometry?.features.length) {
      delete content.geojson;
    }

    this.upsert(content);
  }

  open(type: ListProperty, option?: MatListOption) {
    let dialogComponent: ComponentType<unknown>;
    switch (type) {
      case 'timeframes':
        dialogComponent = DatasetFormTimeframeComponent;
        break;
      case 'people':
        dialogComponent = DatasetFormPersonComponent;
        break;
      case 'organisations':
        dialogComponent = DatasetFormOrganisationComponent;
        break;
      case 'links':
        dialogComponent = DatasetFormLinkComponent;
        break;
      default:
        return;
    }

    this.dialog
      .open(dialogComponent, {
        width: '480px',
        data: option?.value,
        disableClose: true,
      })
      .afterClosed()
      .subscribe((value: Timeframe | Person | Organisation | Link | null) => {
        if (!value) {
          return;
        }

        const content = JSON.parse(JSON.stringify(this.dataset()?.content));
        const values = content[type] || [];

        if (option) {
          const index = this.dataset()?.content[type]?.indexOf(option.value);
          if (index !== undefined && index !== -1) {
            values[index] = value;
          }
        } else {
          values.push(value);
        }

        content[type] = values.length ? values : undefined;

        this.upsert(content);
      });
  }

  addLabels() {
    this.dialog
      .open(DatasetFormLabelComponent, {
        width: '480px',
        disableClose: true,
      })
      .afterClosed()
      .subscribe(async (labels: Label[]) => {
        if (!labels?.length) {
          return;
        }

        for (const label of labels) {
          try {
            await firstValueFrom(this.labelService.addToDataset(this.dataset()!.id, label.id));
          } catch (err) {
            if ((err as HttpErrorResponse).status === 409) {
              continue;
            }

            console.error(err);
            this.notificationService.error(`Failed to add label '${label.title}'.`);
          }
        }

        this.labels.reload();
      });
  }

  async removeLabels(model: SelectionModel<MatListOption>) {
    for (const option of model.selected) {
      const label = option.value;
      try {
        await firstValueFrom(this.labelService.removeFromDataset(this.dataset()!.id, label.id));
      } catch (err) {
        console.error(err);
        this.notificationService.error(`Failed to remove label '${label.title}'.`);
      }
    }

    this.labels.reload();
    model.clear();
  }

  remove(type: ListProperty, options: MatListOption[]) {
    const values = (
      this.dataset()?.content[type] as unknown[] | undefined
    )?.filter((v) => options.findIndex((o) => o.value === v) === -1);

    const content = JSON.parse(JSON.stringify(this.dataset()?.content));
    content[type] = values?.length ? values : undefined;

    this.upsert(content);
  }

  createFiles(event: Event) {
    const element = event.target as HTMLInputElement;
    const files: FileList | null = element.files;
    if (!files) {
      return;
    }

    this.dialog
      .open(DatasetFormAttachmentCreateComponent, {
        width: '480px',
        data: {
          datasetId: this.dataset()?.id,
          files: files,
        },
        disableClose: true,
      })
      .afterClosed()
      .subscribe(() => {
        element.value = '';
        this.files.reload();
      });
  }

  updateFiles(options: MatListOption[]) {
    const attachments = [];
    for (const option of options) {
      const attachment = this.files
        .value()
        ?.items.find((a) => a.id === option.value);
      if (attachment) {
        attachments.push(attachment);
      }
    }

    if (!attachments.length) {
      return;
    }

    this.dialog
      .open(DatasetFormAttachmentComponent, {
        width: '480px',
        data: attachments,
        disableClose: true,
      })
      .afterClosed()
      .subscribe((updatedAttachments: Attachment[] | null) => {
        if (!updatedAttachments) {
          return;
        }

        this.files.reload();
      });
  }

  deleteFiles(model: SelectionModel<MatListOption>) {
    const ids = model.selected.map((o) => o.value as string);
    const attachments = this.files
      .value()
      ?.items.filter((a) => ids.includes(a.id));

    this.dialog
      .open(DatasetFormAttachmentDeleteComponent, {
        width: '480px',
        data: attachments,
        disableClose: true,
      })
      .afterClosed()
      .subscribe((deletedAttachments: Attachment[] | null) => {
        if (!deletedAttachments) {
          return;
        }

        this.files.reload();
        model.clear();
      });
  }

  track(obj: unknown) {
    return JSON.stringify(obj);
  }

  drop(type: ListProperty, event: CdkDragDrop<unknown[]>) {
    if (event.previousIndex === event.currentIndex) {
      return;
    }

    const content = JSON.parse(JSON.stringify(this.dataset()?.content));
    const values = content[type] || [];

    const p = values[event.previousIndex];
    values[event.previousIndex] = values[event.currentIndex];
    values[event.currentIndex] = p;

    content[type] = values.length ? values : undefined;

    this.upsert(content);
  }

  private asKeywordAutocompleteOption(keyword: ScienceKeyword) {
    const objectValues = Object.values(keyword) as string[];
    const id = objectValues.join(' > ');

    return {
      id: id,
      value: keyword,
    } as AutocompleteOption<ScienceKeyword>;
  }

  private upsert(content: DatasetContent) {
    const dataset = this.dataset();

    const fn = dataset?.id
      ? this.datasetService.update(dataset.id, content)
      : this.datasetService.create({
        ...content,
        organisations: [
          {
            name: 'Norwegian Polar Institute',
            roles: [
              'owner',
              'pointOfContact',
              'principalInvestigator',
              'resourceProvider',
            ],
            email: 'data@npolar.no',
            url: 'https://npolar.no',
          },
        ] as Organisation[],
      });

    fn.subscribe({
      next: (dataset: Dataset) => {
        this.router.navigate(['dataset', dataset.id, 'edit']).finally(() => {
          this.dataset.set(dataset);
          this.metadataForm().reset();
        });
      },
      error: (err) => {
        console.error(err);
        this.notificationService.error('Failed to save changes.');
      }
    });
  }
}
